import { useEffect } from 'react';
import { useQuery } from 'react-fetching-library';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import { ALL_ROUTES } from 'config/routes';
import { Button, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeSnackbar,
  enqueueSnackbar,
  navigateToRoute,
  SNACKBAR_VARIANTS,
  updateIsMaintenanceMode,
} from '../redux_store';
import { checkIsMaintenanceMode } from '../helpers/downForMaintenance';

const useStyles = makeStyles(() => ({
  warningAction: {
    minWidth: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  warningCloseAction: {
    position: 'absolute',
    right: 16,
    top: 'calc(50% - 14px)',
  },
}));

const useOrderMetadata = (
  patientUuid,
  accountUuid,
  errorCallback,
  {
    callback: warningCallback,
    actionCallback: warningActionCallback,
    actionTitle: warningActionTitle,
    reorderUuid = 'newOrder',
  },
  successCallback,
) => {
  const { en: { titles, notifications } } = useSelector((state) => state.i18n);
  const dispatch = useDispatch();
  const classes = useStyles();
  const {
    metadata: { currentAccount },
    isDistributionSponsor,
  } = useSelector((state) => state.user);

  const { query, loading } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${accountUuid}/patients/${patientUuid}/create-order-metadata`,
    },
    false,
  ]);

  const onClickDismiss = (key) => () => {
    dispatch(closeSnackbar(key));
  };

  const warningAction = (key) => (
    <div className={classes.warningAction}>
      <Button
        variant='outlined'
        color='primary'
        onClick={warningActionCallback}
      >
        {warningActionTitle}
      </Button>
      <IconButton
        onClick={onClickDismiss(key)}
        size='small'
        className={classes.warningCloseAction}
      >
        <CloseIcon className={classes.closeIcon} />
      </IconButton>
    </div>
  );

  useEffect(() => {
    if (patientUuid) {
      query().then(({ payload: metadata, error: metadataError }) => {
        if (!metadataError && metadata) {
          const { providers = [] } = metadata;
          if (
            metadata &&
            metadata.patientInfo &&
            (metadata.patientInfo.hasDrugAllergies === null ||
              // eslint-disable-next-line no-prototype-builtins
              !metadata.patientInfo.hasOwnProperty('hasDrugAllergies') ||
              metadata.patientInfo.hasDrugAllergies === '')
          ) {
            if (warningCallback) {
              warningCallback();
            }
            dispatch(
              enqueueSnackbar({
                message: notifications.hasDrugAllergies,
                options: {
                  variant: SNACKBAR_VARIANTS.warning,
                  action: warningAction,
                },
              }),
            );
            return;
          }
          if (providers.length) {
            const isItChildAccount = accountUuid
              && accountUuid !== currentAccount.uuid
              && isDistributionSponsor;
            dispatch(navigateToRoute({ route: {
                path: (isItChildAccount ? ALL_ROUTES.order.createDS : ALL_ROUTES.order.create).path,
                params: {
                  currentAccountUuid: currentAccount.uuid,
                  accountUuid: isItChildAccount ? accountUuid : null,
                  patientUuid: patientUuid,
                  reorderUuid: reorderUuid,
                },
                title: titles.createOrder,
              }}));
            if (successCallback) {
              successCallback();
            }
          } else if (errorCallback) {
            errorCallback();
          }
        } else if (metadataError) {
          checkMaintenanceMode();
        }
      });
    }
  }, [patientUuid]);

  const checkMaintenanceMode = async () => {
    const response = await checkIsMaintenanceMode();
    if (response.status === 200) {
      dispatch(updateIsMaintenanceMode(true));
    }
  };

  return {
    loading,
  };
};

export default useOrderMetadata;
